import {
  Box,
  Skeleton,
  Switch,
  Table,
  Text,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Flex,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { FinancialData, Util } from '@finley-technologies/finley-types';

import { FinancialStatementAdHocCalculationsGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { useGetFinancialStatementDataByDateRangeQuery } from '../../requests/financialData.gql.generated';
import {
  numberToFormattedRounded,
  numberWithCommas,
  percentageToPercentageLabel,
} from 'packages/common-frontend/utils';
import { useCreditFacilityContext } from 'packages/credit-facility/hooks/CreditFacilityProvider';
import { colors } from '~/packages/common-frontend/colors';

// helpers
const dateToCadencePeriod = (
  inputDate: Util.FullDateStr,
  reportFrequency: FinancialData.ReportFrequency,
) => {
  const date = commonDate.fullDateStrToDate(inputDate);
  const year = date.getFullYear();
  if (reportFrequency === 'month') {
    const month = date.toLocaleString('default', { month: 'long' });
    const day = date.getDate();
    return `${month} ${day}, ${year}`;
  }
  if (reportFrequency === 'quarter') {
    const quarter = Math.floor((date.getMonth() + 3) / 3);
    return `Q${quarter} ${year}`;
  }
  return `${year}`;
};

const getDeltaCadenceLabel = (
  reportFrequency: FinancialData.ReportFrequency,
) => {
  const cadenceMap = {
    month: ' M/M',
    quarter: ' Q/Q',
    year: ' Y/Y',
  };
  return `Δ ${cadenceMap[reportFrequency]}`;
};

const formatCellValue = (
  cellValue: number | null,
  formatOptions: { isPercentage?: boolean; showFullValue?: boolean },
) => {
  const { showFullValue, isPercentage } = formatOptions;
  if (isPercentage) {
    return cellValue === null
      ? '-%'
      : percentageToPercentageLabel(cellValue * 100, 2);
  }

  if (cellValue === null) return '-';
  return showFullValue
    ? `$${numberWithCommas(cellValue.toFixed(0))}`
    : `$${numberToFormattedRounded(cellValue)}`;
};

const getCellColor = (value: number | null | undefined) => {
  if (!value) return undefined;
  return value > 0 ? colors.green[400] : colors.red[400];
};

const stickyLeftColStyling = {
  position: 'sticky' as const,
  left: 0,
  zIndex: 1,
  backgroundColor: colors.gray[100],
};

export const FinancialStatementsTable = ({
  financialStatement,
  reportFrequency,
  startDate,
  endDate,
}: {
  financialStatement: FinancialData.FinancialStatementType;
  reportFrequency: FinancialData.ReportFrequency;
  startDate: Util.FullDateStr;
  endDate: Util.FullDateStr;
}) => {
  const [showFullValue, setShowFullValue] = useState<boolean>(false);
  const { creditFacilityId } = useCreditFacilityContext();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [previousWindowWidth, setPreviousWindowWidth] = useState<number>(
    window.innerWidth,
  );

  const {
    error,
    data: financialStatementDataByDateRange,
    loading,
  } = useGetFinancialStatementDataByDateRangeQuery({
    variables: {
      startDate,
      endDate,
      financialStatement,
      reportFrequency,
      creditFacilityId,
    },
  });

  useEffect(() => {
    const onResize = () => {
      const currentWindowWidth = window.innerWidth;
      if (
        tableContainerRef.current &&
        previousWindowWidth > currentWindowWidth
      ) {
        tableContainerRef.current.scrollLeft +=
          previousWindowWidth - currentWindowWidth;
      }
      setPreviousWindowWidth(currentWindowWidth);
    };

    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [previousWindowWidth]);

  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollLeft =
        tableContainerRef.current.scrollWidth;
    }
  }, [financialStatementDataByDateRange]);

  if (loading) {
    return <Skeleton h="600px" />;
  }

  if (error) throw error;

  if (
    !financialStatementDataByDateRange?.getFinancialStatementDataByDateRange
  ) {
    return (
      <Text>
        No records were found for the selected date range and report frequency.
        Adjust your selection.
      </Text>
    );
  }

  const financialStatementData =
    financialStatementDataByDateRange.getFinancialStatementDataByDateRange;

  const endOfPeriodDatesAndCadenceDeltas = financialStatementData.metadata.sortedEndOfPeriodDates.flatMap(
    (endOfPeriodDate, idx) =>
      idx > 0
        ? [
            getDeltaCadenceLabel(reportFrequency),
            dateToCadencePeriod(endOfPeriodDate, reportFrequency),
          ]
        : [dateToCadencePeriod(endOfPeriodDate, reportFrequency)],
  );

  const netIncomeAdHocData: FinancialStatementAdHocCalculationsGQLType = financialStatementData.adHocCalculations.find(
    (sectionTotal) => sectionTotal.adHocKey === 'netIncome',
  ) ?? {
    adHocKey: 'netIncome',
    totals: [],
  };

  return (
    <>
      <Flex alignItems="center" mt="8" mb="4">
        <Text mr="2" fontWeight="bold">
          Show full value format
        </Text>
        <Switch
          size="sm"
          isChecked={showFullValue}
          onChange={() => setShowFullValue(!showFullValue)}
          _focus={{ boxShadow: 'none' }}
        />
      </Flex>

      <Box
        borderWidth="thin"
        borderColor={colors.gray[100]}
        overflowX="auto"
        overflowY="auto"
        position="relative"
        ref={tableContainerRef}
      >
        <Table variant="simple">
          <Thead>
            <Tr borderBottom={`2px solid ${colors.black[80]}`} height="52px">
              <Th
                backgroundColor={colors.background[200]}
                style={{
                  ...stickyLeftColStyling,
                  backgroundColor: colors.background[200],
                }}
              />
              {endOfPeriodDatesAndCadenceDeltas.map(
                (endOfPeriodDateOrCadenceDelta, idx) => (
                  <Th
                    whiteSpace="nowrap"
                    key={
                      idx > 0
                        ? `${endOfPeriodDateOrCadenceDelta}-
                        ${endOfPeriodDatesAndCadenceDeltas[idx - 1]}`
                        : endOfPeriodDateOrCadenceDelta
                    }
                    backgroundColor={colors.background[200]}
                    color={colors.black[95]}
                    isNumeric
                  >
                    {endOfPeriodDateOrCadenceDelta}
                  </Th>
                ),
              )}
            </Tr>
          </Thead>
          <Tbody>
            {financialStatementData.sectionKeysToLineItems.map(
              (sectionData) => (
                <React.Fragment key={sectionData.finleySectionKey}>
                  <Tr
                    bg={colors.gray[100]}
                    fontWeight="bold"
                    borderTop={`2px solid ${colors.black[80]}`}
                  >
                    <Td style={stickyLeftColStyling}>
                      {_.capitalize(sectionData.finleySectionKey)}
                    </Td>
                    <Td colSpan={endOfPeriodDatesAndCadenceDeltas.length} />
                  </Tr>
                  {sectionData.lineItemsToValues.map((lineItem) => (
                    <Tr key={lineItem.lineItemDescription} bg={colors.white}>
                      <Td
                        pl={20}
                        style={{
                          ...stickyLeftColStyling,
                          backgroundColor: colors.white,
                        }}
                      >
                        {lineItem.lineItemDescription}
                      </Td>
                      {lineItem.values.map((value, idx) => (
                        <React.Fragment key={value.endOfPeriodDate}>
                          {idx > 0 && (
                            <Td
                              isNumeric
                              style={{
                                color: getCellColor(value.cadenceDelta),
                              }}
                            >
                              {formatCellValue(value.cadenceDelta ?? null, {
                                isPercentage: true,
                              })}
                            </Td>
                          )}
                          <Td isNumeric>
                            {formatCellValue(value.value ?? null, {
                              isPercentage: false,
                              showFullValue,
                            })}
                          </Td>
                        </React.Fragment>
                      ))}
                    </Tr>
                  ))}
                  <Tr
                    bg={colors.gray[100]}
                    borderBottom={`2px solid ${colors.black[80]}`}
                    fontWeight="bold"
                  >
                    <Td pl={20} style={stickyLeftColStyling}>
                      Total
                    </Td>
                    {sectionData.totals.map((total, idx) => {
                      return (
                        <React.Fragment key={total.endOfPeriodDate}>
                          {idx > 0 && (
                            <Td
                              isNumeric
                              style={{
                                color: getCellColor(total.cadenceDelta),
                              }}
                            >
                              {formatCellValue(total.cadenceDelta ?? null, {
                                isPercentage: true,
                              })}
                            </Td>
                          )}
                          <Td isNumeric>
                            {formatCellValue(total.value ?? null, {
                              isPercentage: false,
                              showFullValue,
                            })}
                          </Td>
                        </React.Fragment>
                      );
                    })}
                  </Tr>
                </React.Fragment>
              ),
            )}
            {financialStatement === 'incomeStatement' && (
              <Tr
                bg={colors.gray[100]}
                borderBottom={`2px solid ${colors.black[80]}`}
                fontWeight="bold"
              >
                <Td>Net Income</Td>
                {netIncomeAdHocData.totals.map((total, idx) => {
                  return (
                    <React.Fragment key={total.endOfPeriodDate}>
                      {idx > 0 && (
                        <Td
                          isNumeric
                          style={{ color: getCellColor(total.cadenceDelta) }}
                        >
                          {formatCellValue(total.cadenceDelta ?? null, {
                            isPercentage: true,
                          })}
                        </Td>
                      )}
                      <Td isNumeric>
                        {formatCellValue(total.value ?? null, {
                          isPercentage: false,
                          showFullValue,
                        })}
                      </Td>
                    </React.Fragment>
                  );
                })}
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
    </>
  );
};
